/**
 * The assembled voyage world: a chart, a pace, a settled sea and a journey law.
 * Everything else in this module is a pure function of one of these plus a timestamp.
 *
 * `world-places` swaps the seed table. Whoever owns progression supplies the curve.
 * Neither touches a line of logic to do it.
 *
 * THE CURVE IS A REQUIRED ARGUMENT AND HAS NO DEFAULT. Round 1 exported a
 * ready-to-use `DEFAULT_VOYAGE_WORLD` paced against a placeholder rate, documented
 * "Deterministic; safe to share", and an integrator wiring it to the real simulation the
 * way the module documented would have shipped a sea crossed in 51 minutes. A default
 * that is wrong for the only real caller is worse than no default, so there is no
 * default: the two worlds this file can build are named for what paces them, and the
 * placeholder one says placeholder in its name.
 */

import type { PlaceCall } from './calls';
import { callBodiesFor, solveCalls } from './calls';
import type { BuildChartOptions, Chart } from './chart';
import { buildChart } from './chart';
import { FIRST_LANDFALL_HOURS } from './constants';
import type { SettledSea } from './settled';
import { settledSea } from './settled';
import type { JourneyLaw } from './journey';
import { DEFAULT_JOURNEY_LAW } from './journey';
import type { PlaceSeed } from './seed';
import { CHARTED_PLACES } from './places';
import { MURRIN_DESTINATIONS, type Destination } from './destinations';
import type { ProgressionCurve, VoyagePacing } from './pacing';
import { LINEAR_PLACEHOLDER_CURVE, derivePacingFromFirstLandfall } from './pacing';

export interface VoyageWorld {
  readonly chart: Chart;
  /** Local Murrin choices are navigable, but deliberately not chart progression. */
  readonly destinations: readonly Destination[];
  readonly pacing: VoyagePacing;
  /**
   * What the boat does once the whole chart is known. Solved from the chart AND the pace,
   * because the round is measured in hours - see settled.ts.
   */
  readonly sea: SettledSea;
  readonly journeyLaw: JourneyLaw;
  /**
   * WHERE THE BOAT ARRIVES, solved once from the chart. CONTRACT 1.5n(a): arrival is a state, and
   * this is the state, sorted by where on the track it begins. Empty when no charted place
   * publishes an extent, in which case the route is the bare spiral it always was.
   */
  readonly calls: readonly PlaceCall[];
}

export interface CreateVoyageWorldOptions {
  /** REQUIRED. The progression curve the pace is solved against. No default exists. */
  readonly curve: ProgressionCurve;
  readonly places?: readonly PlaceSeed[];
  readonly chart?: BuildChartOptions;
  /** Hours of drift before the first place other than home comes into sight. */
  readonly firstLandfallHours?: number;
  readonly journeyLaw?: JourneyLaw;
}

export function createVoyageWorld(options: CreateVoyageWorldOptions): VoyageWorld {
  const chart = buildChart(options.places ?? CHARTED_PLACES, options.chart ?? {});
  const pacing = derivePacingFromFirstLandfall(chart, {
    curve: options.curve,
    firstLandfallHours: options.firstLandfallHours ?? FIRST_LANDFALL_HOURS,
  });
  // THE CALLS ARE SOLVED FROM THE CHART AND THE PACE IS NOT INVOLVED. A call moves WHERE the
  // boat is over a stretch of track distance; it never moves how much track distance an hour
  // buys, so `pacing` above and `sea` below are computed from exactly the numbers they were.
  const calls = solveCalls(callBodiesFor(chart.places), { trackLeagues: chart.lastSightTrackDistance });
  return {
    chart,
    destinations: MURRIN_DESTINATIONS,
    pacing,
    calls,
    // The charted sea ends where the last place is sighted. Past that the boat does not
    // leave and does not circle the rim: it keeps a round of the sea it has learned, at
    // the pace it had when the sea ran out. See settled.ts.
    sea: settledSea(chart, pacing),
    journeyLaw: options.journeyLaw ?? DEFAULT_JOURNEY_LAW,
  };
}

/**
 * GEOMETRY ONLY. Paced by the linear placeholder curve, which no simulation realises.
 *
 * Use it for tests about the track, the chart, journeys and regions - things that do not
 * care what an hour is. Driving it with the simulation's progress source is refused by
 * `resolveVoyage`, and pace-agreement.test.ts asserts that refusal, because that exact
 * pairing is round 1's CL1-R1-03.
 */
export const PLACEHOLDER_VOYAGE_WORLD: VoyageWorld = createVoyageWorld({
  curve: LINEAR_PLACEHOLDER_CURVE,
});
